import { zonedDateKey, type MailCardItem, type Microsoft365MailPayload, type Microsoft365MailView } from '@dbrain/shared';
import { Field, Icons, TextArea, TextInput } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import { cardZone, instantOf, mailTime, senderName } from '@dbrain/app-core/answer-cards';
import { formatBytes } from '@dbrain/app-core/format';
import { mailProblems, type MailFields } from '@dbrain/app-core/microsoft365-write';
import { MailRow } from '../../chat/AnswerCards.js';
import { EditablePeople, PeopleChips } from './People.js';

/**
 * The mail card's body (MsSohbetOnay): Kimden, Kime, Cc (no mockup), Konu, a Reply-To that sends the reply elsewhere
 * (no mockup), the whole text as it goes out, a draft's attachments, and "Yanıtlanan mail · Ayşe Demir, 30 Eki 09:12"
 * folded. Above the card, the answered mail's row as the mail list draws it.
 */

/** The answered mail's row (MsMailListe style), from Graph's facts and the model's one-line summary. */
export function AnsweredMailRow({ payload, view, timeZone }: { payload: Microsoft365MailPayload; view: Microsoft365MailView | null; timeZone: string }) {
  const { t, i18n } = useTranslation();
  const source = view?.source;
  if (!source || !payload.source || (payload.mode !== 'reply' && payload.mode !== 'reply_all')) return null;
  const now = new Date();
  const item: MailCardItem = {
    ref: payload.source.ref,
    from: source.from,
    subject: source.subject,
    receivedAt: source.receivedAt,
    isRead: source.isRead,
    importance: 'normal',
    attachments: source.attachments,
    summary: source.summary,
  };
  // a mail of today says only its time, as in the mockup ("09:12")
  const received = instantOf(source.receivedAt);
  const sameDay = received !== null && zonedDateKey(received, cardZone(timeZone)) === zonedDateKey(now, cardZone(timeZone));
  return (
    <section className="answer-card answer-card--mail m365w-source" aria-label={t('microsoft365.chat.write.source')} data-testid="m365-write-source">
      <ul className="mail-list">
        <MailRow item={item} time={mailTime(source.receivedAt, sameDay, timeZone, i18n.language, now)} waiting />
      </ul>
    </section>
  );
}

export function MailBody({
  payload,
  view,
  timeZone,
  editing,
  fields,
  onFields,
  onPending,
}: {
  /** what the card shows: the card's payload, the person's edited version, or the version that went out */
  payload: Microsoft365MailPayload;
  view: Microsoft365MailView | null;
  timeZone: string;
  editing: boolean;
  fields: MailFields;
  onFields: (fields: MailFields) => void;
  /** an address box holds text that was not added */
  onPending?: (box: string, pending: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const account = view?.from.address ?? null;
  const source = view?.source ?? null;
  const problems = editing ? mailProblems(fields) : [];
  return (
    <div className="m365w-body" data-testid="m365-write-mail">
      <dl className="m365w-fields">
        {view ? (
          <div className="m365w-field" data-testid="m365-write-from">
            <dt>{t('microsoft365.chat.write.fields.from')}</dt>
            <dd>
              <strong>{senderName(view.from)}</strong>
              <span className="m365w-muted">{view.from.address}</span>
            </dd>
          </div>
        ) : null}
        {editing ? null : (
          <>
            <div className="m365w-field" data-testid="m365-write-to">
              <dt>{t('microsoft365.chat.write.fields.to')}</dt>
              <dd>
                <PeopleChips people={payload.to} flags={view?.flags} account={account} />
              </dd>
            </div>
            {payload.cc.length > 0 ? (
              <div className="m365w-field" data-testid="m365-write-cc">
                <dt>{t('microsoft365.chat.write.fields.cc')}</dt>
                <dd>
                  <PeopleChips people={payload.cc} flags={view?.flags} account={account} />
                </dd>
              </div>
            ) : null}
            <div className="m365w-field" data-testid="m365-write-subject">
              <dt>{t('microsoft365.chat.write.fields.subject')}</dt>
              <dd>
                <strong>{payload.subject || t('microsoft365.chat.write.noSubject')}</strong>
              </dd>
            </div>
          </>
        )}
      </dl>
      {source?.replyTo ? (
        <p className="m365w-warning" role="note" data-testid="m365-write-reply-to">
          <Icons.warning size={15} />
          <span>{t('microsoft365.chat.write.replyTo', { address: source.replyTo.address })}</span>
        </p>
      ) : null}
      {editing ? (
        <div className="m365w-edit" data-testid="m365-write-edit">
          <EditablePeople label={t('microsoft365.chat.write.fields.to')} people={fields.to} onChange={(to) => onFields({ ...fields, to })} onPending={onPending} flags={view?.flags} account={account} testId="m365-write-edit-to" />
          <EditablePeople label={t('microsoft365.chat.write.fields.cc')} people={fields.cc} onChange={(cc) => onFields({ ...fields, cc })} onPending={onPending} flags={view?.flags} account={account} testId="m365-write-edit-cc" />
          <Field label={t('microsoft365.chat.write.fields.subject')} error={problems.includes('subject') ? t('microsoft365.chat.write.problems.subject') : undefined}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={fields.subject}
                maxLength={300}
                onChange={(e) => onFields({ ...fields, subject: e.target.value })}
                data-testid="m365-write-edit-subject"
              />
            )}
          </Field>
          <Field label={t('microsoft365.chat.write.fields.body')} error={problems.includes('body') ? t('microsoft365.chat.write.problems.body') : undefined}>
            {({ id, describedBy, invalid }) => (
              <TextArea
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                rows={9}
                value={fields.body}
                onChange={(e) => onFields({ ...fields, body: e.target.value })}
                data-testid="m365-write-edit-body"
              />
            )}
          </Field>
          {problems.filter((p) => p !== 'subject' && p !== 'body').map((problem) => (
            <p key={problem} className="m365w-edit__error" role="alert" data-testid="m365-write-problem">
              {t(`microsoft365.chat.write.problems.${problem}`)}
            </p>
          ))}
        </div>
      ) : (
        <div className="m365w-text" data-testid="m365-write-body">
          {payload.body}
        </div>
      )}
      {payload.mode === 'draft' && source && source.attachments.length > 0 ? (
        <div className="m365w-attachments" data-testid="m365-write-attachments">
          <span className="m365w-muted">{t('microsoft365.chat.write.draftAttachments')}</span>
          {source.attachments.map((a, n) => (
            <span key={`${a.name}-${n}`} className="mail-tag" title={formatBytes(a.size, i18n.language)}>
              <Icons.attach size={12} />
              {a.name}
            </span>
          ))}
        </div>
      ) : null}
      {source && payload.mode !== 'draft' && source.text ? (
        <details className="m365w-answered" data-testid="m365-write-answered">
          <summary>
            <Icons.chevronRight size={13} className="m365w-answered__chevron" />
            {t('microsoft365.chat.write.answered', { name: senderName(source.from), time: mailTime(source.receivedAt, false, timeZone, i18n.language, new Date()) })}
          </summary>
          <div className="m365w-answered__text">{source.text}</div>
        </details>
      ) : null}
      {payload.mode === 'reply' || payload.mode === 'reply_all' ? (
        // Outlook's reply carries the thread under the text, as any reply from Outlook does (security review)
        <p className="m365w-quiet m365w-thread" data-testid="m365-write-thread">
          {t('microsoft365.chat.write.thread')}
        </p>
      ) : null}
    </div>
  );
}
